import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Dumbbell, Video } from "lucide-react";

import { servicePath, useSiteConfig } from "../hooks/use-site-config";
import type { PublicDirectory, PublicService } from "../types";
import { earliestOpenTime, specialistsFor } from "../utils/directory";
import { minutesLabel, openTimeLabel, servicePriceLabel } from "../utils/labels";
import { card, figure, label, orb, primaryOrb, tag } from "./section";
import { glowClass } from "./site-theme";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type ServiceCardProps = {
  service: PublicService;
  directory: PublicDirectory;
  /** The first card of a grid: it takes the glow and the primary arrow. */
  lead?: boolean;
  className?: string;
};

/**
 * One booking service as the trainer set it up: its length on a tag, its name and description,
 * the soonest open time and the price. A service with no price shows none.
 */
export const ServiceCard = ({ service, directory, lead, className }: ServiceCardProps) => {
  const site = useSiteConfig();
  const Icon = service.online ? Video : Dumbbell;
  const price = servicePriceLabel(service, directory.business.currency);
  const next = earliestOpenTime(specialistsFor([service], directory));

  return (
    <Link
      to={servicePath(site.slug, service.id)}
      className={cn(
        card,
        lead && glowClass,
        "group flex flex-col outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-h-[260px]",
        className,
      )}
    >
      <span className={tag}>
        <Icon aria-hidden className="size-3.5" />
        {minutesLabel(service.durationMinutes)}
      </span>
      <b className="mt-5 text-[26px] leading-[1.1] font-medium tracking-[-0.03em]">{service.name}</b>
      {service.description ? (
        <p className="mt-2 line-clamp-2 text-muted-foreground">{service.description}</p>
      ) : null}
      <div className="mt-auto flex items-end justify-between gap-3 pt-6">
        <div className="min-w-0">
          {next ? <span className={label}>{openTimeLabel(next, directory)}</span> : null}
          {price ? <div className={cn(figure, "mt-1 text-[40px]")}>{price}</div> : null}
        </div>
        <span className={lead ? primaryOrb : cn(orb, "group-hover:bg-primary group-hover:text-primary-foreground")}>
          <ArrowUpRight
            aria-hidden
            className="size-[18px] transition-transform duration-200 group-hover:rotate-45 motion-reduce:transition-none"
          />
        </span>
      </div>
    </Link>
  );
};

/**
 * How wide a card sits on the grid's six desktop columns, so no row ends on an empty seat: rows
 * of three, with the leftover one or two rows up front taken in halves.
 */
export const serviceCardSpan = (index: number, count: number) => {
  const rest = count % 3;
  const wide = count < 3 ? count : rest === 0 ? 0 : rest === 2 ? 2 : 4;
  return cn(
    count === 1 ? "lg:col-span-6" : index < wide ? "lg:col-span-3" : "lg:col-span-2",
    // Two columns on a tablet: an odd count lets the first card take the whole row.
    count % 2 === 1 && index === 0 && "sm:col-span-2",
  );
};

const grid = "grid gap-3 sm:grid-cols-2 lg:grid-cols-6";

/** Service cards in a grid that always fills its rows. */
export const ServiceGrid = ({
  services,
  directory,
  lead,
  className,
}: {
  services: PublicService[];
  directory: PublicDirectory;
  lead?: boolean;
  className?: string;
}) => (
  <div className={cn(grid, className)}>
    {services.map((service, index) => (
      <ServiceCard
        key={service.id}
        service={service}
        directory={directory}
        lead={lead && index === 0}
        className={serviceCardSpan(index, services.length)}
      />
    ))}
  </div>
);

/** What the grid holds while the trainer's services are on their way. */
export const ServiceGridSkeleton = ({ className }: { className?: string }) => (
  <div className={cn(grid, className)}>
    {Array.from({ length: 3 }, (_, index) => (
      <Skeleton key={index} className={cn("h-[260px] rounded-[28px]", serviceCardSpan(index, 3))} />
    ))}
  </div>
);
